import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  useTheme,
  Typography,
  Stack,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import UPDATE_USER from '../../../Apollo/mutations/updateUser';
import { useSnackbar } from '../../snackbar';
// import { CustomAvatar } from '../../custom-avatar';

// --- Avataaars Options ---

const AVATAR_STYLE = 'avataaars';

const TOPS = [
  'bigHair',
  'bob',
  'bun',
  'curly',
  'curvy',
  'dreads',
  'frida',
  'frizzle',
  'fro',
  'longButNotTooLong',
  'miaWallace',
  'shaggy',
  'shaggyMullet',
  'shavedSides',
  'shortCurly',
  'shortFlat',
  'shortRound',
  'shortWaved',
  'sides',
  'straight01',
  'straight02',
  'straightAndStrand',
  'theCaesar',
  'theCaesarAndSidePart',
  'winterHat01',
  'winterHat02',
  'winterHat03',
  'winterHat04',
  'hat',
  'turban',
  'hijab',
];

const ACCESSORIES = [
  'none',
  'eyepatch',
  'kurt',
  'prescription01',
  'prescription02',
  'round',
  'sunglasses',
  'wayfarers',
];

const HAIR_COLORS = [
  '2c1b18',
  '4a312c',
  '724133',
  'a55728',
  'c93305',
  'd6b370',
  'f59797',
  'ecdcbf',
  'ffffff',
  'e8e1e1',
].map((c) => c.toUpperCase());

const FACIAL_HAIR = ['none', 'beardLight', 'beardMajestic', 'beardMedium', 'moustacheFancy', 'moustacheMagnum'];

const CLOTHING = [
  'blazerAndShirt',
  'blazerAndSweater',
  'collarAndSweater',
  'graphicShirt',
  'shirtCrewNeck',
  'shirtScoopNeck',
  'shirtVNeck',
  'hoodie',
  'overall',
];

const CLOTHING_COLORS = [
  '262e33',
  '65c9ff',
  '5199e4',
  '25557c',
  'e6e6e6',
  '929598',
  '3c4f5c',
  'b1e2ff',
  'a7ffc4',
  'ffdeb5',
].map((c) => c.toUpperCase());

const EYES = [
  'default',
  'closed',
  'cry',
  'eyeRoll',
  'happy',
  'hearts',
  'side',
  'squint',
  'surprised',
  'wink',
  'winkWacky',
];

const EYEBROWS = [
  'default',
  'angry',
  'angryNatural',
  'flatNatural',
  'frownNatural',
  'raisedExcited',
  'sadConcerned',
  'unibrowNatural',
  'upDown',
  'upDownNatural',
];

const MOUTH = [
  'default',
  'concerned',
  'disbelief',
  'eating',
  'grimace',
  'sad',
  'screamOpen',
  'serious',
  'smile',
  'tongue',
  'twinkle',
  'vomit',
];

const SKIN_COLORS = ['f8d25c', 'fd9841', 'ffdbb4', 'edb98a', 'd08b5b', 'ae5d29', '614335'].map((c) => c.toUpperCase());

const BACKGROUND_COLORS = ['b6e3f4', 'c0aede', 'd1d4f9', 'ffd5dc', 'ffdfbf', 'ffffff', 'transparent'];

// --- Translations ---

// --- Translations ---

const OPTION_LABELS: Record<string, string> = {
  // Tops
  bigHair: 'Großes Haar',
  bob: 'Bob',
  bun: 'Dutt',
  curly: 'Lockig',
  curvy: 'Wellig',
  dreads: 'Dreads',
  frida: 'Frida',
  frizzle: 'Gekräuselt',
  fro: 'Afro',
  longButNotTooLong: 'Lang (Mittel)',
  miaWallace: 'Mia W.',
  shaggy: 'Zottelig',
  shaggyMullet: 'Zotteliger Vokuhila',
  shavedSides: 'Seiten rasiert',
  shortCurly: 'Kurz & Lockig',
  shortFlat: 'Kurz & Flach',
  shortRound: 'Kurz & Rund',
  shortWaved: 'Kurz & Wellig',
  sides: 'Seiten',
  straight01: 'Glatt 1',
  straight02: 'Glatt 2',
  straightAndStrand: 'Glatt & Strähne',
  theCaesar: 'Caesar',
  theCaesarAndSidePart: 'Caesar & Scheitel',
  winterHat01: 'Wintermütze 1',
  winterHat02: 'Wintermütze 2',
  winterHat03: 'Wintermütze 3',
  winterHat04: 'Wintermütze 4',
  hat: 'Hut',
  turban: 'Turban',
  hijab: 'Hidschab',

  // Accessories
  none: 'Keine',
  kurt: 'Kurt',
  eyepatch: 'Augenklappe',
  prescription01: 'Brille 1',
  prescription02: 'Brille 2',
  round: 'Rund',
  sunglasses: 'Sonnenbrille',
  wayfarers: 'Wayfarers',

  // Facial Hair
  beardLight: 'Leichter Bart',
  beardMajestic: 'Vollbart (Majestätisch)',
  beardMedium: 'Mittelgroßer Bart',
  moustacheFancy: 'Schnurrbart (Schick)',
  moustacheMagnum: 'Schnurrbart (Magnum)',

  // Clothing
  blazerAndShirt: 'Blazer & Hemd',
  blazerAndSweater: 'Blazer & Pullover',
  collarAndSweater: 'Kragen & Pullover',
  graphicShirt: 'Grafik-Shirt',
  shirtCrewNeck: 'Rundhals-Shirt',
  shirtScoopNeck: 'Ausschnitt-Shirt',
  shirtVNeck: 'V-Ausschnitt',
  hoodie: 'Hoodie',
  overall: 'Overall',

  // Eyes
  default: 'Standard',
  closed: 'Geschlossen',
  cry: 'Weinen',
  eyeRoll: 'Augenrollen',
  happy: 'Glücklich',
  hearts: 'Herzen',
  side: 'Seitlich',
  squint: 'Zusammengekniffen',
  surprised: 'Überrascht',
  wink: 'Zwinkern',
  winkWacky: 'Zwinkern (Verrückt)',

  // Eyebrows
  angry: 'Wütend',
  angryNatural: 'Wütend (Natürlich)',
  flatNatural: 'Flach (Natürlich)',
  frownNatural: 'Stirnrunzeln',
  raisedExcited: 'Aufgeregt',
  sadConcerned: 'Traurig / Besorgt',
  unibrowNatural: 'Monobraue',
  upDown: 'Auf & Ab',
  upDownNatural: 'Auf & Ab (Natürlich)',

  // Mouth
  concerned: 'Besorgt',
  disbelief: 'Ungläubig',
  eating: 'Essen',
  grimace: 'Grimasse',
  sad: 'Traurig',
  screamOpen: 'Schreien',
  serious: 'Ernst',
  smile: 'Lächeln',
  tongue: 'Zunge raus',
  twinkle: 'Zwinkern',
  vomit: 'Übel',
};

const t = (key: string) => OPTION_LABELS[key] || key;
interface AvatarEditorProps {
  open: boolean;
  onClose: () => void;
  username: string;
  currentAvatar?: string;
  currentConfig?: string;
}

export default function AvatarEditor({ open, onClose, username, currentAvatar, currentConfig }: AvatarEditorProps) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [seed, setSeed] = useState(username);

  // Customization State
  const [top, setTop] = useState('bigHair');
  const [accessories, setAccessories] = useState('none');
  const [hairColor, setHairColor] = useState(HAIR_COLORS[0]);
  const [facialHair, setFacialHair] = useState('none');
  const [clothing, setClothing] = useState('blazerAndShirt');
  const [clothesColor, setClothesColor] = useState(CLOTHING_COLORS[0]);
  const [eyes, setEyes] = useState('default');
  const [eyebrows, setEyebrows] = useState('default');
  const [mouth, setMouth] = useState('default');
  const [skinColor, setSkinColor] = useState(SKIN_COLORS[0]);
  const [backgroundColor, setBackgroundColor] = useState('b6e3f4');

  // Tab state
  const [tabIndex, setTabIndex] = useState(0);

  const [updateUser, { loading }] = useMutation(UPDATE_USER);

  const handleRandomize = () => {
    setSeed(Math.random().toString(36).substring(7));
    setTop(TOPS[Math.floor(Math.random() * TOPS.length)]);
    setAccessories(ACCESSORIES[Math.floor(Math.random() * ACCESSORIES.length)]);
    setHairColor(HAIR_COLORS[Math.floor(Math.random() * HAIR_COLORS.length)]);
    setFacialHair(FACIAL_HAIR[Math.floor(Math.random() * FACIAL_HAIR.length)]);
    setClothing(CLOTHING[Math.floor(Math.random() * CLOTHING.length)]);
    setClothesColor(CLOTHING_COLORS[Math.floor(Math.random() * CLOTHING_COLORS.length)]);
    setEyes(EYES[Math.floor(Math.random() * EYES.length)]);
    setEyebrows(EYEBROWS[Math.floor(Math.random() * EYEBROWS.length)]);
    setMouth(MOUTH[Math.floor(Math.random() * MOUTH.length)]);
    setSkinColor(SKIN_COLORS[Math.floor(Math.random() * SKIN_COLORS.length)]);
    setBackgroundColor(BACKGROUND_COLORS[Math.floor(Math.random() * BACKGROUND_COLORS.length)]);
  };

  useEffect(() => {
    if (currentConfig && open) {
      try {
        const config = JSON.parse(currentConfig);
        if (config.seed) setSeed(config.seed);
        if (config.top) setTop(config.top);
        if (config.accessories) setAccessories(config.accessories);
        if (config.hairColor) setHairColor(config.hairColor);
        if (config.facialHair) setFacialHair(config.facialHair);
        if (config.clothing) setClothing(config.clothing);
        if (config.clothesColor) setClothesColor(config.clothesColor);
        if (config.eyes) setEyes(config.eyes);
        if (config.eyebrows) setEyebrows(config.eyebrows);
        if (config.mouth) setMouth(config.mouth);
        if (config.skinColor) setSkinColor(config.skinColor);
        if (config.backgroundColor) setBackgroundColor(config.backgroundColor);
      } catch (e) {
        console.error('Failed to parse avatar config', e);
      }
    } else if (open && !currentAvatar) {
      // Only randomize if opening new and no avatar/config exists
      handleRandomize();
    }
  }, [currentConfig, open, currentAvatar]);

  const getAvatarUrl = () => {
    // We explicitly build the URL with all params to ensure what you see is what you get
    const baseUrl = `https://api.dicebear.com/9.x/${AVATAR_STYLE}/svg`;
    const params = new URLSearchParams({
      seed,
      top,
      hairColor,
      clothing,
      clothesColor,
      eyes,
      eyebrows,
      mouth,
      skinColor,
      backgroundColor,
    });

    if (accessories === 'none') {
      params.append('accessoriesProbability', '0');
    } else {
      params.append('accessories', accessories);
      params.append('accessoriesProbability', '100');
    }

    if (facialHair === 'none') {
      params.append('facialHairProbability', '0');
    } else {
      params.append('facialHair', facialHair);
      params.append('facialHairProbability', '100');
    }

    return `${baseUrl}?${params.toString()}`;
  };

  const handleSave = async () => {
    try {
      const newAvatarUrl = getAvatarUrl();
      // Collect current config to save
      const avatarConfig = JSON.stringify({
        seed,
        top,
        accessories,
        hairColor,
        facialHair,
        clothing,
        clothesColor,
        eyes,
        eyebrows,
        mouth,
        skinColor,
        backgroundColor,
      });

      const { data } = await updateUser({
        variables: {
          username,
          variables: {
            avatar: newAvatarUrl,
            avatarConfig,
          },
        },
      });

      if (data?.updateUser?.token) {
        localStorage.setItem('token', data.updateUser.token);
      }

      enqueueSnackbar('Avatar erfolgreich aktualisiert!');
      onClose();
      window.location.reload();
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Fehler beim Aktualisieren des Avatars', { variant: 'error' });
    }
  };

  const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  // Helper to render color circle
  const renderColorSelect = (colors: string[], current: string, onChange: (c: string) => void) => (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
      {colors.map((color) => (
        <Box
          key={color}
          onClick={() => onChange(color)}
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: color === 'transparent' ? 'transparent' : `#${color}`,
            backgroundImage:
              color === 'transparent'
                ? 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)'
                : 'none',
            backgroundSize: '10px 10px',
            backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px',
            cursor: 'pointer',
            border: current === color ? `3px solid ${theme.palette.primary.main}` : '1px solid #ddd',
            boxShadow: current === color ? theme.shadows[8] : 'none',
            '&:hover': { transform: 'scale(1.1)' },
            transition: 'all 0.2s',
          }}
        />
      ))}
    </Stack>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Avatar anpassen</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
          {/* Preview */}
          <Box
            sx={{
              width: 150,
              height: 150,
              borderRadius: '50%',
              overflow: 'hidden',
              border: `4px solid ${theme.palette.primary.main}`,
              backgroundColor: theme.palette.grey[100],
            }}
          >
            <img src={getAvatarUrl()} alt="Avatar Vorschau" style={{ width: '100%', height: '100%' }} />
          </Box>

          <Button startIcon={<RefreshIcon />} onClick={handleRandomize} variant="outlined" size="small">
            Zufällig
          </Button>

          {/* Options Tabs */}
          <Box sx={{ width: '100%', mt: 2 }}>
            <Tabs value={tabIndex} onChange={handleChangeTab} variant="fullWidth" sx={{ mb: 2 }}>
              <Tab label="Kopf" />
              <Tab label="Gesicht" />
              <Tab label="Stil" />
            </Tabs>

            {/* HEAD TAB */}
            {tabIndex === 0 && (
              <Stack spacing={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Frisur / Kopfbedeckung</InputLabel>
                  <Select value={top} label="Frisur / Kopfbedeckung" onChange={(e) => setTop(e.target.value)}>
                    {TOPS.map((tVal) => (
                      <MenuItem key={tVal} value={tVal}>
                        {t(tVal)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography variant="body2">Haarfarbe</Typography>
                {renderColorSelect(HAIR_COLORS, hairColor, setHairColor)}

                <Typography variant="body2">Hautfarbe</Typography>
                {renderColorSelect(SKIN_COLORS, skinColor, setSkinColor)}
              </Stack>
            )}

            {/* FACE TAB */}
            {tabIndex === 1 && (
              <Stack spacing={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Augen</InputLabel>
                  <Select value={eyes} label="Augen" onChange={(e) => setEyes(e.target.value)}>
                    {EYES.map((tVal) => (
                      <MenuItem key={tVal} value={tVal}>
                        {t(tVal)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Augenbrauen</InputLabel>
                  <Select value={eyebrows} label="Augenbrauen" onChange={(e) => setEyebrows(e.target.value)}>
                    {EYEBROWS.map((tVal) => (
                      <MenuItem key={tVal} value={tVal}>
                        {t(tVal)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Mund</InputLabel>
                  <Select value={mouth} label="Mund" onChange={(e) => setMouth(e.target.value)}>
                    {MOUTH.map((tVal) => (
                      <MenuItem key={tVal} value={tVal}>
                        {t(tVal)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small">
                  <InputLabel>Bart</InputLabel>
                  <Select value={facialHair} label="Bart" onChange={(e) => setFacialHair(e.target.value)}>
                    {FACIAL_HAIR.map((tVal) => (
                      <MenuItem key={tVal} value={tVal}>
                        {t(tVal)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            )}

            {/* STYLE TAB */}
            {tabIndex === 2 && (
              <Stack spacing={3}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Hintergrund
                </Typography>
                {renderColorSelect(BACKGROUND_COLORS, backgroundColor, setBackgroundColor)}

                <FormControl fullWidth size="small">
                  <InputLabel>Kleidung</InputLabel>
                  <Select value={clothing} label="Kleidung" onChange={(e) => setClothing(e.target.value)}>
                    {CLOTHING.map((tVal) => (
                      <MenuItem key={tVal} value={tVal}>
                        {t(tVal)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography variant="body2">Kleidungsfarbe</Typography>
                {renderColorSelect(CLOTHING_COLORS, clothesColor, setClothesColor)}

                <FormControl fullWidth size="small">
                  <InputLabel>Accessoires (Brille)</InputLabel>
                  <Select
                    value={accessories}
                    label="Accessoires (Brille)"
                    onChange={(e) => setAccessories(e.target.value)}
                  >
                    {ACCESSORIES.map((tVal) => (
                      <MenuItem key={tVal} value={tVal}>
                        {t(tVal)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Abbrechen
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={loading}>
          Speichern
        </Button>
      </DialogActions>
    </Dialog>
  );
}
